import BoxKit from "@/shared/components/kit/box";
import CustomButton from "@/shared/components/kit/button";
import AlertDialog from "@/shared/components/kit/modal";
import StackKit from "@/shared/components/kit/stack";
import TypoGraphyKit from "@/shared/components/kit/typoGraphy";
import { Box, InputAdornment, TextField, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import PaymentIcon from "@mui/icons-material/Payment";
import OtpInput from "@/shared/components/common/otp";
import StackedImageSlider from "@/shared/components/common/slider";
import ContentModal from "./contentModal";
import { WithdrawWallet } from "@/shared/components/common/icons";
import CustomSelectNormal from "@/shared/components/common/select/selectNormal";
import { useMutation, useQuery } from "@tanstack/react-query";
import AddIcon from "@mui/icons-material/Add";
import { postPreWithdraw, postWithdraw, postWithdrawConfirm, } from "@/api/user/home";
import { useForm } from "react-hook-form";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { UserBankAccountsDaum } from "@/types/api/user/home.type.data";
import { useNavigate } from "react-router";
import { numberSeparator, toPersianNumber, } from "@/hooks/useThousandsSeparator";
import { bankCards, getBankInfo } from "../addShetabCard/data.card.bank";
import useMaskedCardNumber from "@/hooks/useMaskedCardNumber";

const bankImageMap: Record<string, string> = {
  "بانک ملت": "/bank-cards/mellat.jpg",
  "بانک ملی ایران": "/bank-cards/melli.jpg",
  "بانک سامان": "/bank-cards/saman.jpg",
};
/**
 * ContentWithdrawFromWallet
 *
 * کامپوننت مدیریت برداشت وجه از کیف پول کاربر.
 *
 * ویژگی‌ها:
 * - ورود مبلغ برداشت و انتخاب کارت بانکی
 * - نمایش کارت‌های بانکی کاربر با اسلایدر و امکان افزودن کارت جدید
 * - ارسال درخواست برداشت اولیه به سرور
 * - تایید برداشت با کد OTP ارسال شده به شماره کاربر
 * - مدیریت مراحل مختلف تراکنش با useState و AlertDialog
 * - نمایش اطلاعات تراکنش در مودال پیش‌نمایش قبل از ارسال نهایی
 *
 * @param {Object} props
 * @param {UserBankAccountsDaum[] | undefined} props.dataUserBankAccounts - لیست کارت‌های بانکی کاربر
 * @returns JSX.Element
 */
// Task Response:

// توضیحات بهینه‌سازی و پیشنهادات:
//
// 1. داده‌ها از داده‌های بانک کاربر واکشی می‌شوند و برای نمایش کارت‌ها و انتخاب کارت استفاده می‌شوند.
// 2. مراحل برداشت با یک state به نام `step` مدیریت می‌شوند:
//    - step 0: فرم ورود مبلغ و انتخاب کارت
//    - step 1: نمایش مودال پیش‌نمایش تراکنش و تایید
//    - step 2: وارد کردن OTP و تایید نهایی
// 3. از React Query برای ارسال درخواست‌های API استفاده شده است.
// 4. کارت‌ها در بخش اسلایدر با StackedImageSlider نمایش داده می‌شوند و کارت انتخاب شده با `selected` مدیریت می‌شود.
// 5. اعداد به فارسی تبدیل شده و به صورت ریال فرمت می‌شوند.
// 6. پیشنهاد: استفاده از Skeleton یا Loader در زمان ارسال درخواست‌ها و بررسی خطاها برای بهبود UX.

const ContentWithdrawFromWallet = ({ dataUserBankAccounts, }: { dataUserBankAccounts: UserBankAccountsDaum[] | undefined; }) => {
  const navigate = useNavigate();

  const { mutate: fetchTransactions, data: postAmount, error, } = useMutation({
    mutationFn: postPreWithdraw,
    onSuccess: () => {
      handleNextStep(1);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const [phoneNumber, setPhoneNumber] = useState<string>("");

  const { mutate: fetchPostWithdrawConfirm, data: postPostWithdrawConfirmData, error: errorPostWithdrawConfirm, } = useMutation({
    mutationFn: postWithdrawConfirm,
    onSuccess: () => {
      handleNextStep(0);
    },
    onError: (error: AxiosError<{ message: string }>) => {
      toast.error(error?.response?.data?.message);
    },
  });

  const { mutate: fetchWithdraw, data: postWithdrawData, error: errorWithdraw, } = useMutation({
    mutationFn: postWithdraw,
    onSuccess: (data: any) => {
      setPhoneNumber(data?.data?.phone);
      handleNextStep(2);
    },
  });

  const { handleSubmit, register } = useForm();
  const [amountState, setAmountState] = useState("");
  const [step, setStep] = React.useState(0);
  const [selected, setSelected] = useState<string>("option1");
  const [selectedRequest, setSelectedRequest] = useState("");
  const [sliderIndex, setSliderIndex] = React.useState(0);
  const [otpData, setOtpData] = useState("");

  function toEnglishNumber(str: string) {
    return str.replace(/[۰-۹]/g, (d) => "۰۱۲۳۴۵۶۷۸۹".indexOf(d).toString());
  }

  const handleChange = (e: any) => {
    setAmountState(e.target.value);
  };

  const handleNextStep = (id: number) => {
    setStep(id);
  };

  const onSubmit = (data: any) => {
    const cleanedAmount = amountState ? amountState.toString().replace(/[٬,]/g, "").replace(/\s*ریال\s*/g, "").replace(/\s/g, "").trim() : "0";
    const requestBody = { card: selectedRequest, amount: cleanedAmount, };
    fetchTransactions(requestBody);
  };

  // open modal Otp
  const onSubmitAccept = () => {
    const requestBody = { card: selectedRequest, amount: amountState, };
    fetchWithdraw(requestBody);
  };

  const onSubmitConfirm = () => {
    const requestBody = { card: selectedRequest, amount: amountState, otp: otpData, };
    fetchPostWithdrawConfirm(requestBody);
  };

  const bankAccounts = dataUserBankAccounts ?? [];
  const itemsImg = bankAccounts.map((item) => {
    const bankInfo = getBankInfo(item.card_number);
    const imageFileName = bankInfo?.card || "default.jpg";
    return {
      id: item.id,
      url: `/bank-cards/${imageFileName}`,
      title: item.card_number,
      footer: item.user,
    };
  });

  useEffect(() => {
    const newIndex = itemsImg.findIndex(
      (item) => `${item.id}` === `${selected}`
    );
    if (newIndex !== -1 && newIndex !== sliderIndex) {
      setSliderIndex(newIndex);
    }
  }, [selected, itemsImg, sliderIndex]);

  return (
    <StackKit className="w-full">
      <StackKit direction={{ md: 'row', sm: 'column-reverse' }} className=" tems-center justify-between" gap={10} >
        {/* Form Section */}
        <form onSubmit={handleSubmit(onSubmit)} className="w-full order-2 md:order-1">
          <StackKit direction="column" className="w-full h-auto md:h-[456px] justify-around rounded-lg p-4 sm:p-6"
            style={{ background: "linear-gradient(137.33deg, rgba(0,0,0,0.6) -17.41%, rgba(64,64,64,0.38) 24.51%, rgba(115,103,240,0.2) 75.94%, rgba(0,0,0,0.4) 111.96%)", }}
          >
            <BoxKit className="w-full flex justify-start text-right">
              <TypoGraphyKit variant="body1">
                در این بخش شما میتوانید درخواست برداشت از کیف پول خود را ثبت نمایید. مبلغ  مورد نظر پس از بررسی و تایید به حساب انتخاب شده توسط  شما واریز خواهدشد.
              </TypoGraphyKit>
            </BoxKit>

            {/* Amount Input */}
            <StackKit
              direction={{ xs: "column", sm: "row" }}
              className="w-full"
              sx={{
                "& > :not(style) ~ :not(style)": {
                  marginLeft: "0 !important",
                },
              }}
              gap={{ xs: 1, sm: 2 }}
            >
              <BoxKit sx={{
                width: { xs: "100%", sm: "40%" },
                display: "flex",
                alignItems: "center"
              }}>
                <TypoGraphyKit variant="body1" sx={{ textAlign: "right", width: "100%" }}>
                  مبلغ مورد نظر جهت برداشت از کیف پول را وارد نمایید.
                </TypoGraphyKit>
              </BoxKit>

              <Box sx={{ width: { xs: "100%", sm: "60%" } }}>
                <TextField
                  fullWidth
                  sx={{
                    bgcolor: "#383838",
                    borderRadius: "10px",
                    '& .MuiInputBase-root': {
                      fontSize: { xs: "0.875rem", sm: "1rem" }
                    }
                  }}
                  placeholder={`${numberSeparator(200000)}`}
                  value={
                    amountState
                      ? `${toPersianNumber(numberSeparator(amountState))} `
                      : ""
                  }
                  onChange={(e) => {
                    const englishNumber = toEnglishNumber(e.target.value);
                    const rawValue = englishNumber.replace(/[^0-9]/g, "");
                    if (Number(rawValue) <= 100000000) {
                      setAmountState(rawValue);
                    }
                  }}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <TypoGraphyKit variant="body2">ریال</TypoGraphyKit>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
            </StackKit>

            {/* Card Select */}
            <StackKit
              direction={{ xs: "column", sm: "row" }}
              className="w-full"
              gap={{ xs: 1, sm: 2 }}
            >
              <BoxKit sx={{
                width: { xs: "100%", sm: "40%" },
                display: "flex",
                alignItems: "center",
                justifyContent: { xs: "flex-start", sm: "flex-end" }
              }}>
                <TypoGraphyKit variant="body1" sx={{ textAlign: "right", width: "100%" }}>
                  کارت مورد نظر جهت برداشت وجه را انتخاب نمایید.
                </TypoGraphyKit>
              </BoxKit>

              <Box sx={{ width: { xs: "100%", sm: "60%" } }}>
                <CustomSelectNormal
                  label=""
                  value={selected}
                  onChange={(e) => {
                    const selectedId = Number(e.target.value);
                    const selectedItem = dataUserBankAccounts?.find(
                      (item) => item.id === selectedId
                    );
                    setSelected(`${selectedId}`);
                    setSelectedRequest(selectedItem?.card_number ?? "");
                  }}
                  options={dataUserBankAccounts ?? []}
                  getOptionLabel={(item) => `${item.card_number}`}
                  getOptionValue={(item) => item.id}

                />
              </Box>
            </StackKit>

            {/* Footer Buttons */}
            <StackKit
              direction="row"
              className="w-full justify-between items-center flex-wrap mt-4"
              sx={{
                "& > :not(style) ~ :not(style)": {
                  marginLeft: "0 !important",
                },
              }}
            >
              <BoxKit className="mb-3 sm:mb-0">
                <TypoGraphyKit variant="body1" color="#666">
                  سقف درخواست برداشت از کیف پول: {numberSeparator(50000000)} ریال
                </TypoGraphyKit>
              </BoxKit>

              <CustomButton className="w-full sm:w-[92px] h-[48px]" type="submit">
                ادامه
              </CustomButton>
            </StackKit>
          </StackKit>
        </form>

        {/* Bank Card / Slider Section */}
        <StackKit direction="column" className="w-fulloverflow-hidden order-1 md:order-2">
          {dataUserBankAccounts?.length === 0 ? (
            <StackKit alignItems="center" className="gap-4">
              <StackKit className="w-full sm:w-[238px] h-[160px] bg-[#2E2E2E] cursor-pointer flex justify-center items-center rounded-lg" onClick={() => navigate("/wallet-reports/add-shetab-card")}>
                <TypoGraphyKit variant="body1">
                  کارت بانکی وجود ندارد
                </TypoGraphyKit>
              </StackKit>

              <StackKit direction="row" className="cursor-pointer justify-center" onClick={() => navigate("/wallet-reports/add-shetab-card")}>
                <StackKit
                  direction="row"
                  className="w-full max-w-[200px] h-[75px] px-2.5 flex justify-center items-center rounded-lg"
                  style={{ background: "linear-gradient(90deg, rgba(94,94,94,0.4) -46.82%, rgba(56,56,56,0.1) 161.36%)", }}
                >
                  <Typography variant="body1" className="text-[12px]">
                    افزودن کارت بانکی
                  </Typography>
                  <AddIcon className="text-white ml-1" />
                </StackKit>
              </StackKit>
            </StackKit>
          ) : (
              <StackKit
                  direction="column"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  width="100%"
                  className="w-full"
                  gap={2}
              >
                <StackKit width="100%" display="flex" justifyContent="center" alignItems="center" marginX={'auto'}>
                  <StackedImageSlider
                      activeIndex={sliderIndex}
                      onSlideChange={(swiper) => {
                        const index = swiper.realIndex;
                        const selectedItem = itemsImg[index];
                        setSelected(`${selectedItem?.id}`);
                      }}
                      items={itemsImg}
                      renderItem={(item) => (
                          <div className="relative w-full h-[200px] sm:h-[250px] md:h-full mx-auto">
                            <img
                                src={item.url}
                                alt="کارت"
                                onError={(e) => ((e.target as HTMLImageElement).src = "/bank-cards/default.jpg")}
                                className="w-full h-full object-cover rounded-xl"
                            />
                            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/50 text-white px-3 py-2 rounded-lg text-sm">
                              {item.title}
                            </div>
                            <div className="absolute bottom-3 right-3 bg-white/80 text-[#333] px-2.5 py-1.5 rounded-md text-xs">
                              {item.footer}
                            </div>
                          </div>
                      )}
                  />
                </StackKit>

                <TypoGraphyKit className="mt-3 text-center text-sm sm:text-base">
                  کارت مورد نظر جهت واریز وجه را انتخاب نمایید
                </TypoGraphyKit>
              </StackKit>
          )}
        </StackKit>
      </StackKit>
      <AlertDialog
        paragraph={`شما در حال انتقال وجه از کیف پول خود به کیف پول اعتباری به شماره ${toPersianNumber(selectedRequest)}  هستید. جزییات تراکنش به شرح زیر میباشد:`}
        agreeText={"تایید و ارسال درخواست"}
        disagreeText={"باز گشت"}
        onAgree={onSubmitAccept}
        onDisagree={() => handleNextStep(0)}
        isHeader={true}
        header={
          <StackKit direction={"row"} alignItems={"center"} gap={2}>
            <BoxKit>
              <WithdrawWallet width={24} height={24} fill={"#fff"} />
            </BoxKit>
            <TypoGraphyKit variant="body1">برداشت از کیف پول</TypoGraphyKit>
          </StackKit>
        }
        onClose={() => { }}
        open={step === 1}
      >
        <ContentModal postAmount={postAmount} />
      </AlertDialog>
      <AlertDialog
        paragraph={`جهت تکمیل فرآیند کد امنیتی وارد شده به شماره تلفن ${toPersianNumber(useMaskedCardNumber(phoneNumber))} را وارد نمایید.`}
        agreeText={"ادامه فرایند"}
        disagreeText={"باز گشت"}
        onAgree={onSubmitConfirm}
        onDisagree={() => handleNextStep(0)}
        isHeader={true}
        header={
          <StackKit direction={"row"} alignItems={"center"} gap={2}>
            <BoxKit>
              <PaymentIcon />
            </BoxKit>
            <TypoGraphyKit variant="body1">انتقال موجودی</TypoGraphyKit>
          </StackKit>
        }
        onClose={() => { }}
        open={step === 2}
      >
        <OtpInput onChange={(e) => console.log("onChange console", e)} onComplete={(e) => setOtpData(e)} />
      </AlertDialog>
    </StackKit>
  );


};

export default ContentWithdrawFromWallet;
